/* eslint-disable @typescript-eslint/explicit-member-accessibility */
/* eslint-disable @typescript-eslint/member-ordering */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, DestroyRef, computed, inject, input, output } from '@angular/core';
import { ChatMessage } from '../../models/chat.model';

/**
 * EVA Chat Content Component
 * 
 * Renders individual bot messages with action buttons:
 * - Copy to clipboard
 * - Like/Dislike feedback
 * - Read aloud (text-to-speech)
 * - Suggestion chips (when provided)
 * 
 * @example
 * <nalco-eva-chat-content
 *   [message]="botMessage"
 *   [suggestions]="suggestionList"
 *   (suggestionSelected)="onSuggestion($event)"
 *   (copyClicked)="onCopy($event)"
 *   (likeClicked)="onLike($event)"
 *   (dislikeClicked)="onDislike($event)">
 * </nalco-eva-chat-content>
 */
@Component({
    selector: 'nalco-eva-chat-content',
    templateUrl: './eva-chat-content.component.html',
    styleUrl: './eva-chat-content.component.scss',
    standalone: false
})
export class EvaChatContentComponent {
    private readonly destroyRef = inject(DestroyRef);

    /** The chat message to display */
    readonly message = input.required<ChatMessage>();

    /** Array of suggestion chips to display below message */
    readonly suggestions = input<string[]>([]);

    /** Current selected feedback state for the message */
    readonly selectedFeedback = input<'like' | 'dislike' | null>(null);

    /** Emits when a suggestion chip is clicked */
    readonly suggestionSelected = output<string>();

    /** Emits when copy button is clicked */
    readonly copyClicked = output<ChatMessage>();

    /** Emits when like button is clicked */
    readonly likeClicked = output<ChatMessage>();

    /** Emits when dislike button is clicked */
    readonly dislikeClicked = output<ChatMessage>();

    /** Emits when text-to-speech button is clicked */
    readonly ttsClicked = output<ChatMessage>();

    /** Indicates if copy action was successful (for visual feedback) */
    copySuccess = false;

    /** Indicates if text-to-speech is currently reading */
    isReading = false;

    /** Web Speech API synthesis instance */
    private synthesis: SpeechSynthesis;

    /** Current speech utterance being spoken */
    private currentUtterance: SpeechSynthesisUtterance | null = null;

    /**
     * Get formatted timestamp for display
     * @returns Time string in HH:MM format
     */
    readonly formattedTime = computed(() => {
        const msg = this.message();
        if (!msg?.timestamp) {
            return '';
        }
        const date = new Date(msg.timestamp);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    });

    /**
     * Get message text content
     * @returns Message data as string
     */
    readonly messageText = computed(() => {
        return this.message()?.content?.data ?? '';
    });

    constructor() {
        this.synthesis = window.speechSynthesis;

        // Register cleanup on destroy
        this.destroyRef.onDestroy(() => {
            this.stopReading();
        });
    }

    /**
     * Handle suggestion chip click
     * @param suggestion - The suggestion text that was clicked
     */
    onSuggestionClick(suggestion: string): void {
        this.suggestionSelected.emit(suggestion);
    }

    /**
     * Copy message content to clipboard
     * Uses modern Clipboard API with fallback for older browsers
     */
    onCopy(): void {
        const textContent = this.extractTextContent(this.messageText());

        if (navigator.clipboard && window.isSecureContext) {
            // Use modern clipboard API
            navigator.clipboard.writeText(textContent)
                .then(() => {
                    this.showCopyFeedback();
                })
                .catch((err) => {
                    console.error('Failed to copy:', err);
                    this.fallbackCopy(textContent);
                });
        } else {
            // Fallback for older browsers or non-secure contexts
            this.fallbackCopy(textContent);
        }

        this.copyClicked.emit(this.message());
    }

    /**
     * Extract plain text from HTML content
     * Removes all HTML tags
     * 
     * @param content - Content to extract text from
     * @returns Plain text string
     * @private
     */
    private extractTextContent(content: any): string {
        if (typeof content === 'string') {
            // Remove HTML tags and decode entities
            const div = document.createElement('div');
            div.innerHTML = content;
            return div.textContent || div.innerText || '';
        }
        return String(content || '');
    }

    /**
     * Fallback copy method for browsers without Clipboard API
     * Uses deprecated document.execCommand
     * 
     * @param text - Text to copy
     * @private
     */
    private fallbackCopy(text: string): void {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
            const successful = document.execCommand('copy');
            if (successful) {
                this.showCopyFeedback();
            }
        } catch (err) {
            console.error('Fallback copy failed:', err);
        }

        document.body.removeChild(textArea);
    }

    /**
     * Show copy success feedback (green checkmark)
     * Automatically hides after 2 seconds
     * @private
     */
    private showCopyFeedback(): void {
        this.copySuccess = true;
        setTimeout(() => {
            this.copySuccess = false;
        }, 2000);
    }

    /**
     * Handle like button click
     */
    onLike(): void {
        this.likeClicked.emit(this.message());
    }

    /**
     * Handle dislike button click
     */
    onDislike(): void {
        this.dislikeClicked.emit(this.message());
    }

    isFeedbackSelected(feedback: 'like' | 'dislike'): boolean {
        return this.selectedFeedback() === feedback;
    }

    /**
     * Toggle text-to-speech reading
     * Starts reading if not active, stops if active
     */
    onReadAloud(): void {
        if (this.isReading) {
            this.stopReading();
        } else {
            this.startReading();
        }
        this.ttsClicked.emit(this.message());
    }

    /**
     * Start text-to-speech reading of message content
     * @private
     */
    private startReading(): void {
        if (!('speechSynthesis' in window)) {
            console.warn('Text-to-speech not supported in this browser');
            return;
        }

        // Stop any ongoing speech
        this.stopReading();

        // Extract text content from message
        const textToRead = this.extractTextContent(this.messageText());

        if (!textToRead.trim()) {
            return;
        }

        // Create utterance
        this.currentUtterance = new SpeechSynthesisUtterance(textToRead);

        // Configure speech properties
        this.currentUtterance.rate = 1.0;    // Normal speed
        this.currentUtterance.pitch = 1.0;   // Normal pitch
        this.currentUtterance.volume = 1.0;  // Full volume
        this.currentUtterance.lang = 'en-US';

        // Event handlers
        this.currentUtterance.onstart = () => {
            this.isReading = true;
        };

        this.currentUtterance.onend = () => {
            this.isReading = false;
            this.currentUtterance = null;
        };

        this.currentUtterance.onerror = (event) => {
            console.error('Speech synthesis error:', event);
            this.isReading = false;
            this.currentUtterance = null;
        };

        // Start speaking
        this.synthesis.speak(this.currentUtterance);
    }

    /**
     * Stop text-to-speech reading
     * Cancels any active speech synthesis
     * @private
     */
    private stopReading(): void {
        if (this.synthesis) {
            this.synthesis.cancel();
        }
        this.isReading = false;
        this.currentUtterance = null;
    }
}
